/** Detalhe do SKU (Fase 2): ritmo, estoque por local, preço e margem por anúncio (passos) e anúncios do SKU. */
import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { KpiCard } from '../../components/KpiCard'
import { colunasAnuncio } from './colunas'
import { carregarPedidosAnuncio } from './fonte'
import { Mc, Produto, StatusChip, TabelaOrdenavel } from './componentes'
import { brl2, CATEGORIAS, dataBr, nomeLoja, num, PARCELAS, pct } from './logic'
import type { Anuncio, NatalData, PedidosAnuncio } from './types'

export function DetalheSku({ d, sku, anuncio, onFechar }: { d: NatalData; sku: string | null; anuncio?: { loja: string; id: string } | null; onFechar: () => void }) {
  const theme = useTheme()
  const k = d.skus.find((x) => x.sku === sku)
  const ans = useMemo(() => d.anuncios.filter((a) => a.comps.some(([s]) => s === sku)).sort((x, y) => y.vendas.fat7 - x.vendas.fat7), [d, sku])
  const colunas = useMemo(() => colunasAnuncio(d), [d])
  if (!k) return null
  const serie = d.calendario.map((c, i) => ({
    dia: dataBr(c), meta: k.meta_dia[i],
    vendido: i <= d.i_hoje ? Object.values(k.vendas).reduce((a, s) => a + s.pecas[i], 0) : null,
  }))
  const comPreco = ans.filter((a) => a.preco_sim && !a.preco_sim.erro)
  return (
    <Dialog open={!!sku} onClose={onFechar} maxWidth="xl" fullWidth sx={{ zIndex: (t) => t.zIndex.modal + 4 }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <b>{k.sku}</b><Typography color="text.secondary">· {CATEGORIAS[k.categoria]}</Typography><StatusChip s={k.calc.status} parado={k.calc.parado} />
          {k.perene && <Chip size="small" label={`perene · meta ${pct(k.meta_pct)}`} />}
          <Chip size="small" color={k.custo_fonte === 'cmvs_lote_novo' ? 'success' : 'warning'} label={k.custo_fonte === 'cmvs_lote_novo' ? 'custo confirmado' : 'custo estimado'} title={k.custo_fonte} />
        </Stack>
        <Typography variant="caption" color="text.secondary">{k.descricao}</Typography>
        <IconButton aria-label="Fechar" onClick={onFechar} sx={{ position: 'absolute', right: 8, top: 8 }}>×</IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <KpiCard label="Comprado" value={`${num(k.qtd_comprada)} pç`} hint={k.perene ? `meta até ${dataBr(d.premissas.data_limite)}: ${num(k.qtd_meta)} (${pct(k.meta_pct)})` : `meta: 100% até ${dataBr(d.premissas.data_limite)}`} />
            <KpiCard label="Estoque hoje" value={`${num(PARCELAS.reduce((a, [p]) => a + (k.estoque[p] ?? 0), 0))} pç`} hint={PARCELAS.map(([p, n]) => `${n.replace('Galpão Cabreúva', 'galpão')} ${num(k.estoque[p])}`).join(' · ')} />
            <KpiCard label="Meta de hoje" value={`${num(k.calc.meta_hoje)} pç`} hint={`a chegar: ${num(k.calc.a_chegar)} pç`} />
            <KpiCard label={`Ritmo ${d.premissas.janela_ritmo_dias} dias`} value={k.calc.ritmo == null ? 'sem leitura' : k.calc.ritmo.toFixed(2).replace('.', ',')} hint={`${num(k.calc.vend7)} vendidas / meta ${num(k.calc.meta7)}`} />
            <KpiCard label="Real × alvo acumulado" value={`${pct(k.calc.real_acum)} × ${pct(k.calc.alvo_acum)}`} hint={k.conc?.mediana ? `concorrentes ${brl2(k.conc.min)} – ${brl2(k.conc.max)}` : `preço planejado ${brl2(k.preco_plan_peca * k.un_venda)} (${k.preco_fonte})`} />
          </Stack>

          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={serie} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={theme.palette.divider} vertical={false} />
                <XAxis dataKey="dia" tick={{ fontSize: 11 }} interval={9} />
                <YAxis tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={(v) => (typeof v === 'number' ? num(v) : String(v))} />
                {k.lotes.map((l) => <ReferenceLine key={l.embarque} x={dataBr(l.data)} stroke={theme.palette.secondary.main} strokeDasharray={l.confirmada ? undefined : '5 3'} />)}
                {d.i_hoje >= 0 && <ReferenceLine x={dataBr(d.hoje)} stroke={theme.palette.text.primary} />}
                <Bar dataKey="vendido" name="Vendido (pç)" fill={theme.palette.primary.light} />
                <Line dataKey="meta" name="Meta do dia (pç)" stroke={theme.palette.secondary.main} dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          <Box>
            <Typography variant="h6">Preço e margem por anúncio</Typography>
            <Typography variant="caption" color="text.secondary">MC antes de ads (ads entra no DRE abaixo da MC) · piso MC {pct(d.premissas.mc_piso_pct)} até {dataBr(d.premissas.data_liquidacao)}, depois {pct(d.premissas.mc_pos_liquidacao)} (perenes ficam no piso normal) · ↓ = {pct(d.premissas.passo_baixa_pct_espaco)} do espaço até o piso · ↑ = +{pct(d.premissas.passo_alta_pct)}</Typography>
            <TabelaOrdenavel vazio="Nenhum anúncio ativo com simulação de preço." linhas={comPreco} colunas={[
              { label: 'Anúncio', valor: (a: Anuncio) => a.titulo, render: (a: Anuncio) => <Produto a={a} largura={260} /> },
              { label: 'Loja', valor: (a: Anuncio) => a.loja, render: (a: Anuncio) => nomeLoja(d, a.loja) },
              { label: 'Preço atual', valor: (a: Anuncio) => a.preco, render: (a: Anuncio) => brl2(a.preco), alinhar: 'right' },
              { label: 'MC', valor: (a: Anuncio) => a.preco_sim?.mc ?? null, render: (a: Anuncio) => <Mc v={a.preco_sim?.mc} />, alinhar: 'right' },
              { label: '↓ 1 passo', valor: (a: Anuncio) => a.preco_sim?.baixo ?? null, render: (a: Anuncio) => brl2(a.preco_sim?.baixo), alinhar: 'right' },
              { label: 'MC ↓', valor: (a: Anuncio) => a.preco_sim?.mc_baixo ?? null, render: (a: Anuncio) => <Mc v={a.preco_sim?.mc_baixo} />, alinhar: 'right' },
              { label: '↑ passo', valor: (a: Anuncio) => a.preco_sim?.alto ?? null, render: (a: Anuncio) => brl2(a.preco_sim?.alto), alinhar: 'right' },
              { label: 'MC ↑', valor: (a: Anuncio) => a.preco_sim?.mc_alto ?? null, render: (a: Anuncio) => <Mc v={a.preco_sim?.mc_alto} />, alinhar: 'right' },
              { label: 'Piso', valor: (a: Anuncio) => a.preco_sim?.piso ?? null, render: (a: Anuncio) => brl2(a.preco_sim?.piso), alinhar: 'right' },
              { label: 'Taxas', render: (a: Anuncio) => <Typography variant="caption" color="text.secondary">comissão {pct(a.preco_sim?.comissao_pct, 1)} · frete {brl2(a.preco_sim?.frete)} · imposto {pct(a.preco_sim?.imposto_pct, 1)}<br />{a.preco_sim?.fonte}</Typography> },
            ]} />
          </Box>

          <PedidosMargem key={k.sku} d={d} ans={ans} anuncio={anuncio} />

          <Box>
            <Typography variant="h6">Anúncios deste SKU</Typography>
            <Typography variant="caption" color="text.secondary">Todas as lojas, inclusive kits que usam este SKU. A imagem e o título abrem o anúncio.</Typography>
            <TabelaOrdenavel vazio="Nenhum anúncio encontrado para este SKU." linhas={ans} colunas={colunas} />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

const CABECALHO: [string, boolean][] = [['Data', false], ['Pedido', false], ['NF', false], ['Qtd', true], ['Receita', true], ['Desconto', true], ['Desc. vendedor', true], ['Rebate', true],
  ['Taxa marketplace', true], ['Frete', true], ['ICMS', true], ['DIFAL', true], ['PIS/COFINS', true], ['Impostos', true], ['CMV', true],
  ['MC oficial', true], ['MC %', true], ['Parte do SKU', true], ['MC no painel', true]]

/** Pedidos da margem oficial (ecommerce_financial.margens) que formam a venda e a MC do anúncio no painel (spec §16). */
function PedidosMargem({ d, ans, anuncio }: { d: NatalData; ans: Anuncio[]; anuncio?: { loja: string; id: string } | null }) {
  const vendendo = useMemo(() => ans.filter((a) => a.vendas.pecas_temp > 0), [ans])
  const [sel, setSel] = useState<{ loja: string; id: string } | null>(() => {
    const dado = anuncio && vendendo.find((a) => a.loja === anuncio.loja && a.id === anuncio.id)
    const a = dado || [...vendendo].sort((x, y) => y.vendas.fat_temp - x.vendas.fat_temp)[0]
    return a ? { loja: a.loja, id: a.id } : null
  })
  const [periodo, setPeriodo] = useState<'7d' | 'temp'>('temp')
  const [res, setRes] = useState<PedidosAnuncio | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)
  useEffect(() => {
    if (!sel) return
    let vivo = true
    setCarregando(true); setErro(null)
    const a = vendendo.find((x) => x.loja === sel.loja && x.id === sel.id)
    carregarPedidosAnuncio(sel.loja, a?.item_id ?? sel.id, periodo, a?.model_id ? a.comps.map((c) => c[0]) : [])
      .then((r) => { if (vivo) setRes(r) })
      .catch((e: unknown) => { if (vivo) { setRes(null); setErro(e instanceof Error ? e.message : String(e)) } })
      .finally(() => { if (vivo) setCarregando(false) })
    return () => { vivo = false }
  }, [sel, periodo, vendendo])
  return (
    <Box>
      <Typography variant="h6">Pedidos que formam a margem</Typography>
      <Typography variant="caption" color="text.secondary">MC realizada = margem oficial do sistema por pedido (ecommerce_financial.margens). A MC da tabela de preço acima é a simulação no preço atual. Coluna Rebate: informativo, não entra na conta oficial hoje.</Typography>
      {vendendo.length === 0 ? <Typography variant="body2" sx={{ mt: 1 }}>Nenhum anúncio deste SKU com venda na temporada.</Typography> : (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ my: 1 }}>
            {vendendo.map((a) => (
              <Chip key={`${a.loja}|${a.id}`} size="small" clickable color={sel?.loja === a.loja && sel.id === a.id ? 'primary' : 'default'}
                label={`${nomeLoja(d, a.loja)} · ${a.item_id ?? a.id}${a.variacao ? ` · ${a.variacao}` : ''}`} title={a.titulo} onClick={() => setSel({ loja: a.loja, id: a.id })} />
            ))}
            <ToggleButtonGroup size="small" exclusive value={periodo} onChange={(_, v: '7d' | 'temp' | null) => v && setPeriodo(v)}>
              <ToggleButton value="7d">7 dias</ToggleButton>
              <ToggleButton value="temp">Temporada</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {erro && <Alert severity="warning">{erro}</Alert>}
          {!erro && !carregando && res && res.linhas.length === 0 && <Typography variant="body2">Nenhum pedido deste anúncio no período.</Typography>}
          {!erro && res && res.linhas.length > 0 && (
            <TabelaPedidos res={res} carregando={carregando} />
          )}
        </>
      )}
    </Box>
  )
}

/** Tabela dos pedidos da margem de um anúncio (detalhe do SKU e Visão geral → faturamento em níveis). */
export function TabelaPedidos({ res, carregando, alturaMax = '50vh' }: { res: PedidosAnuncio; carregando: boolean; alturaMax?: string }) {
  return (
    <TableContainer sx={{ maxHeight: alturaMax, opacity: carregando ? 0.5 : 1 }}>
      <Table size="small" stickyHeader>
        <TableHead><TableRow>{CABECALHO.map(([t, dir]) => <TableCell key={t} align={dir ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }} title={t === 'Rebate' ? 'informativo: não entra na conta oficial hoje' : undefined}>{t}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {res.linhas.map((p, i) => (
            <TableRow key={`${p.pedido}-${i}`} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataBr(p.dia)}</TableCell>
              <TableCell>{p.pedido}</TableCell>
              <TableCell>{p.nf || '—'}</TableCell>
              <TableCell align="right">{num(p.qtd)}</TableCell>
              <TableCell align="right">{brl2(p.receita)}</TableCell>
              <TableCell align="right">{brl2(p.desconto)}</TableCell>
              <TableCell align="right">{brl2(p.desconto_vendedor)}</TableCell>
              <TableCell align="right">{brl2(p.rebate)}</TableCell>
              <TableCell align="right">{brl2(p.taxa_marketplace)}</TableCell>
              <TableCell align="right">{brl2(p.frete)}</TableCell>
              <TableCell align="right">{brl2(p.icms)}</TableCell>
              <TableCell align="right">{brl2(p.difal)}</TableCell>
              <TableCell align="right">{brl2(p.pis_cofins)}</TableCell>
              <TableCell align="right">{brl2(p.impostos)}</TableCell>
              <TableCell align="right">{brl2(p.cmv)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {p.pendente
                  ? <Chip size="small" color="warning" variant="outlined" label="margem pendente" title={`Pedido pago (${p.status || 'sem status'}) ainda sem margem oficial: o envio está pendente ou retido pelo marketplace. MC % e MC do painel são estimadas (MC simulada do anúncio).`} />
                  : brl2(p.mc_oficial)}
                {p.nao_fecha && <Chip size="small" color="warning" label="não fecha" sx={{ ml: 0.5 }} title={`componentes somam ${brl2(p.soma_componentes)}; diferença ${brl2(p.diferenca)}`} />}
              </TableCell>
              <TableCell align="right" title={p.pendente ? 'estimada' : undefined}><Mc v={p.mc_pct} />{p.pendente ? ' est.' : ''}</TableCell>
              <TableCell align="right">{pct(p.parte_sku)}</TableCell>
              <TableCell align="right">{brl2(p.mc_painel)}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ '& td': { fontWeight: 700 } }} title="É o número que o painel mostra para este anúncio">
            <TableCell colSpan={3}>Total · {res.total.pedidos} pedidos</TableCell>
            <TableCell colSpan={2} align="right">{brl2(res.total.fat)}</TableCell>
            <TableCell colSpan={11} />
            <TableCell align="right"><Mc v={res.total.mc_pct} /></TableCell>
            <TableCell />
            <TableCell align="right">{brl2(res.total.mc)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
